/**
 * UYAP bağlantı kurtarma: yeniden bağlanma temposu ve kota-dolu bildirimi.
 *
 * Oturum düştüğünde görev HATAYA DÜŞÜRÜLMEZ; bot kaldığı yerde durup bağlanmayı
 * dener. Denemeler giderek uzayan aralıklarla yapılır (login-spam ban'ını önlemek
 * için) ve ASLA vazgeçilmez. Bağlantı gelince askıdaki istek kaldığı yerden çalışır.
 *
 * Saf tutulur (store/DOM bağımlılığı yok) ki birim testten geçebilsin.
 */

/**
 * Yeniden bağlanma denemeleri arası bekleme (ms). Son değer TAVANDIR; denemeler
 * sonsuza dek bu aralıkla sürer. İlk denemeler kısa (geçici kopmalar hızlı toparlansın),
 * sonrakiler uzun (ban riski olmasın).
 *
 * İlk adım 3 sn: kurtarma artık koşulsuz olarak en az bir tur bekliyor, tek seferlik
 * geçici bir 401'in kullanıcıya 15 sn'ye mal olmaması için. Yeniden giriş turu
 * (socket → sunucu → İmzacı → UYAP) pratikte 2-5 sn sürüyor ve bekleme sırasında
 * bağlantı gelirse 1 sn'lik yoklamayla erken çıkılıyor.
 */
export const YENIDEN_BAGLANMA_BEKLEMELERI_MS = [3_000, 15_000, 30_000, 60_000, 120_000];

/** deneme 1'den başlar; dizinin dışına taşınca son (tavan) değeri döner. */
export function yenidenBaglanmaBeklemesiMs(deneme: number): number {
    const i = Math.min(Math.max(deneme - 1, 0), YENIDEN_BAGLANMA_BEKLEMELERI_MS.length - 1);
    return YENIDEN_BAGLANMA_BEKLEMELERI_MS[i];
}

/**
 * Global 401 devre kesici — son emniyet.
 *
 * Oturum bayrağı ya da backoff ileride yine bozulursa bile UYAP'a kısa sürede
 * çok sayıda başarısız istek gitmesini engeller. Gerçek olay: bayrak "bağlı"
 * kalırken kurtarma döngüsü bedelsiz çalıştı, 4 dakika boyunca 14,5 istek/sn
 * gönderildi ve kullanıcı UYAP tarafından IP ban yedi. Bu kesici, o sınıftaki
 * her hatayı sebebinden bağımsız olarak ucundan yakalar.
 *
 * Saf tutulur (store/DOM bağımlılığı yok) ve zaman dışarıdan verilebilir ki
 * birim testte sahte saatle sürülebilsin.
 */
export const DEVRE_KESICI_PENCERE_MS = 60_000;
export const DEVRE_KESICI_ESIK = 20;

let yetkisizZamanlari: number[] = [];
let devreAcik = false;

/** Bir 401 kaydeder. Devre açıldıysa (eşik aşıldıysa) true döner. */
export function yetkisizKaydet(simdi: number = Date.now()): boolean {
    if (devreAcik) {
        return true;
    }
    yetkisizZamanlari = yetkisizZamanlari.filter(zaman => simdi - zaman < DEVRE_KESICI_PENCERE_MS);
    yetkisizZamanlari.push(simdi);
    if (yetkisizZamanlari.length >= DEVRE_KESICI_ESIK) {
        devreAcik = true;
    }
    return devreAcik;
}

export function devreKesiciAcikMi(): boolean {
    return devreAcik;
}

/** Bağlantı düzeldiğinde veya kullanıcı işlemi yeniden başlattığında çağrılır. */
export function devreKesiciSifirla(): void {
    yetkisizZamanlari = [];
    devreAcik = false;
}

export const DEVRE_KESICI_MESAJI =
    'UYAP bağlantısında tekrarlayan oturum hatası alındı. Sunucuya aşırı istek gönderilip ' +
    'erişim engeli (ban) riski oluşmaması için işlemler durduruldu. İmzacı bağlantınızı ve ' +
    'e-imza kartınızı kontrol edip yeniden başlatınız.';

/** UYAP kota dolu hatasının metni (PRTL_GNL_100026-1). */
export const KOTA_DOLU_MESAJI = 'En fazla 5 aktif oturum açabilirsiniz!';

export function kotaDoluHatasiMi(hata: any): boolean {
    const mesaj = String(hata?.message ?? hata ?? '');
    return mesaj.includes(KOTA_DOLU_MESAJI.trim()) || hata?.errorCode === 'PRTL_GNL_100026-1';
}

/**
 * Kota dolduğunda çağrılacak kanca (dialog + işletim sistemi bildirimi + ses).
 * Uygulama kaydeder; kaydedilmezse sessizce atlanır — böylece bu modül store/DOM'a
 * bağlanmaz ve uyap-plugin'i kullanan diğer uygulamalarda da sorunsuz çalışır.
 */
let kotaKancasi: (() => void) | null = null;

export function kotaUyarisiKancasiniAyarla(fn: (() => void) | null): void {
    kotaKancasi = fn;
}

export function kotaUyarisiGoster(): void {
    if (!kotaKancasi) {
        return;
    }
    try {
        kotaKancasi();
    } catch (e) {
        console.warn('Kota uyarısı gösterilemedi:', e);
    }
}

/**
 * Kurtarma sırasındaki durum mesajı ("yeniden bağlanılıyor..." gibi).
 *
 * Kanca üzerinden veriliyor çünkü iki uygulama bunu farklı gösteriyor:
 * vue-imza-app'te UyapStatusCard içinde satır içi bir mesaj, icrapro-app'te
 * toast. Plugin'in hangisinin var olduğunu bilmesi gerekmiyor.
 */
export type DurumMesajiTipi = 'info' | 'success' | 'error';

let durumMesajiKancasi: ((tip: DurumMesajiTipi, metin: string) => void) | null = null;

export function durumMesajiKancasiniAyarla(fn: ((tip: DurumMesajiTipi, metin: string) => void) | null): void {
    durumMesajiKancasi = fn;
}

export function durumMesajiGoster(tip: DurumMesajiTipi, metin: string): void {
    if (!durumMesajiKancasi) {
        return;
    }
    try {
        durumMesajiKancasi(tip, metin);
    } catch (e) {
        console.warn('Durum mesajı gösterilemedi:', e);
    }
}

/**
 * Devre kesici açıldığında çağrılacak kanca (engelleyici dialog + botu durdurma).
 * Kota kancasıyla aynı desen: uygulama kaydeder, kaydedilmezse sessizce atlanır.
 */
let devreKesiciKancasi: (() => void) | null = null;

export function devreKesiciKancasiniAyarla(fn: (() => void) | null): void {
    devreKesiciKancasi = fn;
}

export function devreKesiciUyarisiGoster(): void {
    if (!devreKesiciKancasi) {
        return;
    }
    try {
        devreKesiciKancasi();
    } catch (e) {
        console.warn('Devre kesici uyarısı gösterilemedi:', e);
    }
}

/**
 * Oturum düştüğünde "yeniden bağlan" isteği (DEV-3898).
 *
 * İmzacıda (bot uygulaması) kanca KAYITLIDIR: istek doğrudan yerel `botBaglantisiniSurdur`a
 * gider, socket'e çıkılmaz. Eskiden her kurtarma turu socket'e `connectToUyap` yayınlıyor,
 * kabuk bunu tüm istemcilere geri yayınlıyor ve imzacının KENDİ dinleyicisi görünür (PIN
 * penceresi açan) girişi başlatıyordu; üstelik kabuk <= 2.0.4 olay yükünü atıyor, yani
 * "sessiz" bilgisi hiç ulaşmıyordu. Kanca yoksa (icrapro-app, tarayıcı) olay socket'e
 * `{kaynak: 'kurtarma-arayuz'}` yüküyle yayınlanır (tarayıcıda bot döngüsü yoktur, bkz.
 * UyapRequest); yük ulaşmazsa imzacı eski gibi görünür giriş yapar.
 *
 * bot=true: istek bot döngüsünden (kimse başında değil → sessiz). bot=false: arayüzden
 * (kullanıcı bekliyor → PIN penceresi açılabilir).
 */
let baglantiKurtarmaKancasi: ((bot: boolean) => void) | null = null;

export function baglantiKurtarmaKancasiniAyarla(fn: ((bot: boolean) => void) | null): void {
    baglantiKurtarmaKancasi = fn;
}

/**
 * Kanca kayıtlı mı? Yalnız imzacıda (bot uygulaması) true; icrapro-app'te (tarayıcı) false.
 * UyapRequest "bu istek bot döngüsünden mi" kararını buna (ya da bu süreçte bot döngüsünün
 * kurulu olmasına) bağlar: tarayıcıda bot döngüsü yoktur, ama store'daki player durumu
 * socket'ten yansıdığı için 'oto' görünebilir; o durumda tarayıcı isteği yanlışlıkla bot
 * sayılır, imzacıya SESSİZ 'kurtarma' gider ve imzacı bayat 'bağlı' bayrağıyla yok sayardı
 * (arayüz isteği 48 sn sınırsız bekler). Kanca yalnız bu PR'ın host'unda kaydolur; eski
 * vue-imza-app (karışık yayın penceresi) kaydetmez, orada döngü işareti belirleyicidir.
 */
export function baglantiKurtarmaKancasiKayitliMi(): boolean {
    return baglantiKurtarmaKancasi !== null;
}

/** Kanca kayıtlıysa onu çağırıp true döner; yoksa false (çağıran socket'e yayınlar). */
export function baglantiKurtarmaKancasiniCagir(bot: boolean): boolean {
    if (!baglantiKurtarmaKancasi) {
        return false;
    }
    try {
        baglantiKurtarmaKancasi(bot);
    } catch (e) {
        console.warn('Bağlantı kurtarma isteği iletilemedi:', e);
    }
    return true;
}
